import React, { useEffect, useState } from 'react'

import { useApiKeyActions } from 'redux/actions/ApiKeys'
import { useExportTableActions } from 'redux/actions/ExportTables'
import { useSnackbarActions } from 'redux/actions/Snackbar'

import { ApiKey, ApiKeyType } from 'types/GlobalApiKey'
import { SnackbarMessage } from 'types/GlobalSnackbar'

import { useTranslation } from 'language/translator'
import { useApiKeys } from 'redux/hooks/ApiKeys'
import { useIsManagementCompany } from 'redux/hooks/Customer'
import {
  useExportTables,
  useExportTablesLoading
} from 'redux/hooks/ExportTables'

import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import List from 'components_new/atoms/List'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import SearchField from 'components_new/molecules/SearchField'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SelectSearch from 'components_new/molecules/SelectSearch'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

import CreateApiKeyDialog from 'components_new/organisms/dialogs/CreateApiKeyDialog'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import SettingsTemplate from 'components_new/templates/SettingsTemplate'

import { useAllowAccessExportTables } from 'redux/hooks/Permissions'

const ApiKeyPage = () => {
  const { tryGetAllApiKeys, tryDeleteApiKey } = useApiKeyActions()
  const apiKeys = useApiKeys()
  const exportTablesLoading = useExportTablesLoading()
  const { tryGetAllExportTables } = useExportTableActions()
  const isManagementCompany = useIsManagementCompany()
  const allowAccessExportTables = useAllowAccessExportTables()

  useEffect(() => {
    if (!apiKeys.fetched) {
      tryGetAllApiKeys()
    }

    if (!exportTablesLoading.fetched) {
      tryGetAllExportTables()
    }
  }, [])

  const { t, translateEnum } = useTranslation()
  const { toggleSnackbarOpen } = useSnackbarActions()

  const [apiKeyToDelete, setDeleteApiKey] = useState<ApiKey | null>(null)
  const [createApiKeyOpen, setCreateApiKeyOpen] = useState<boolean>(false)
  const [editKeyOpen, setEditKeyOpen] = useState<string | null>(null)

  const [searchQuery, setSearchQuery] = useState('')

  const filteredApiKeys = apiKeys.keys.filter((apiKey) =>
    apiKey.name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  function getFixedType() {
    if (isManagementCompany && !allowAccessExportTables) {
      return ApiKeyType.DASHBOARD
    } else if (!isManagementCompany && allowAccessExportTables) {
      return ApiKeyType.EXPORT_TABLE
    }

    return undefined
  }

  return (
    <>
      <SettingsTemplate>
        <SectionHeader
          gutterBottom={true}
          title={t('global', 'api_keys')}
          titleAdornment={
            searchQuery ? (
              <Text color="text.secondary">
                {filteredApiKeys.length} {t('search', 'results_found', true)}
              </Text>
            ) : null
          }
        />

        {/*-- search --*/}
        <SearchField
          onClear={() => setSearchQuery('')}
          onSearch={(value) => setSearchQuery(value)}
          placeholder={t('users', 'search_placeholder')}
          size="small"
          sx={{ mb: 1 }}
          value={searchQuery}
        />

        {/*-- table --*/}
        <SimpleTable
          addRow={{
            onClick: () => setCreateApiKeyOpen(true)
          }}
          border={true}
          columns={[
            { title: t('global', 'name'), width: 250 },
            { title: t('global', 'api_key') },
            { title: t('global', 'type') },
            { title: t('global', 'access') },
            { align: 'right' }
          ]}
          hover={true}
          loading={!apiKeys.fetched}
          rows={filteredApiKeys.map((apiKey) => {
            return { cells: [
              <React.Fragment key={0}>{apiKey.name}</React.Fragment>,
              <React.Fragment key={1}>
                {
                  <>
                    {apiKey.value}{' '}
                    <IconButton
                      disabled={apiKey.id === apiKeyToDelete?.id}
                      onClick={() => {
                        navigator.clipboard.writeText(apiKey.value)

                        toggleSnackbarOpen(SnackbarMessage.CopiedToClipboard)
                      }}
                      size="small"
                    >
                      <Icon fontSize="small" name={'CopyAllOutlined'} />
                    </IconButton>
                  </>
                }
              </React.Fragment>,
              <React.Fragment key={2}>
                <Tooltip
                  title={translateEnum('api_key_types', apiKey.type).body}
                >
                  <Chip
                    label={translateEnum('api_key_types', apiKey.type).title}
                    size="small"
                  />
                </Tooltip>
              </React.Fragment>,
              <React.Fragment key={3}>
                {apiKeys.accessTo[apiKey.id]?.length > 0 ? (
                  <Tooltip
                    title={apiKeys.accessTo[apiKey.id]
                      .map((item) => item.title)
                      .join(', ')}
                  >
                    <Text sx={{ cursor: 'default' }}>
                      {apiKeys.accessTo[apiKey.id].length}{' '}
                      {apiKey.type === ApiKeyType.DASHBOARD
                        ? apiKeys.accessTo[apiKey.id].length === 1
                          ? t('global', 'dashboard')
                          : t('global', 'dashboards')
                        : apiKeys.accessTo[apiKey.id].length === 1
                          ? t('export_tables', 'table')
                          : t('export_tables', 'tables')}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text color="text.secondary">{t('global', 'none')}</Text>
                )}
              </React.Fragment>,
              <React.Fragment key={4}>
                <IconButton
                  disabled={
                    apiKey.id === apiKeyToDelete?.id ||
                    apiKey.type === ApiKeyType.DASHBOARD
                  }
                  onClick={() => setEditKeyOpen(apiKey.id)}
                  size="small"
                >
                  <Icon fontSize="small" name="Edit" />
                </IconButton>
                <IconButton
                  disabled={apiKey.id === apiKeyToDelete?.id}
                  onClick={() => setDeleteApiKey(apiKey)}
                  size="small"
                >
                  <Icon fontSize="small" name="Delete" />
                </IconButton>
              </React.Fragment>
            ] }
          })}
        />
      </SettingsTemplate>

      {/*-- dialogs --*/}
      <CreateApiKeyDialog
        fixedType={getFixedType()}
        onClose={() => setCreateApiKeyOpen(false)}
        open={createApiKeyOpen}
      />

      <EditExportTableAccessDialog
        onClose={() => setEditKeyOpen(null)}
        keyId={editKeyOpen}
      />
      <DeleteDialog
        open={apiKeyToDelete !== null}
        onClose={() => setDeleteApiKey(null)}
        onDelete={(cb) => {
          if (apiKeyToDelete) {
            tryDeleteApiKey(apiKeyToDelete.id)
          }

          setDeleteApiKey(null)
          cb()
        }}
        type={t('global', 'api_key')}
        title={apiKeyToDelete?.name as string}
      />
    </>
  )
}

interface EditExportTableAccessDialogProps {
  keyId: string | null
  onClose: () => void
}

const EditExportTableAccessDialog = (
  props: EditExportTableAccessDialogProps
) => {
  const { keyId, onClose } = props

  const { t } = useTranslation()

  const { tryAssignExportTablesToApiKey } = useApiKeyActions()

  const exportTables = useExportTables()

  const access = keyId
    ? exportTables
      .filter((exportTable) => exportTable.api_keys.includes(keyId))
      .map((exportTable) => exportTable.id)
    : []

  const notSelected = exportTables.filter((table) => !access.includes(table.id))
  const selected = exportTables.filter((table) => access.includes(table.id))

  return (
    <SimpleDialog
      actions={
        <Button onClick={onClose} variant={'text'}>
          {t('global', 'close')}
        </Button>
      }
      title={t('export_tables', 'authorized_export_tables_title')}
      open={!!keyId}
      onClose={onClose}
    >
      <SelectSearch
        closeOnSelect={true}
        color="primary"
        disableClearable={true}
        label={t('export_tables', 'add_export_table')}
        onChange={(option) => {
          if (keyId) {
            tryAssignExportTablesToApiKey(
              keyId,
              [...selected.map((selected) => selected.id), option.id],
              []
            )
          }
        }}
        multiple={false}
        options={notSelected.map((table) => ({
          id: table.id,
          title: table.title,
          disabled: !!!table.slug
        }))}
        selected={null}
        size={'medium'}
        sort={true}
        sx={{ mb: 2 }}
      />

      {selected.length > 0 && (
        <List dense sx={{ pt: 0 }}>
          {selected.map((table) => (
            <ListItem
              key={table.id}
              secondaryAction={
                <Tooltip title={t('export_tables', 'remove_export_table')}>
                  <IconButton
                    onClick={() => {
                      if (keyId) {
                        tryAssignExportTablesToApiKey(
                          keyId,
                          selected
                            .map((item) => item.id)
                            .filter((id) => id !== table.id),
                          [table.id]
                        )
                      }
                    }}
                    size="small"
                  >
                    <Icon fontSize="small" name="RemoveCircleOutlineOutlined" />
                  </IconButton>
                </Tooltip>
              }
              sx={{
                px: 0,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ListItemText
                primary={table.title}
                primaryTypographyProps={{
                  fontWeight: 500,
                  ml: 1
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </SimpleDialog>
  )
}

export default ApiKeyPage
